/**
 * Date utility functions for handling dates across timezones
 * 
 * Best practices:
 * - Store dates in UTC in the database
 * - Display dates in the user's local timezone (browser timezone)
 * - Compare dates by calendar date (extract UTC date components)
 */

/**
 * Get the user's timezone from the browser
 * Falls back to 'America/Chicago' if not available (for older browsers)
 */
export const getUserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago';
  } catch {
    return 'America/Chicago';
  }
};

/**
 * Get today's date in the user's timezone as YYYY-MM-DD
 */
export const getTodayInUserTimezone = (): string => {
  const timezone = getUserTimezone();
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  return formatter.format(new Date());
};

/**
 * Convert a UTC date (stored in database) to a calendar date string in user's timezone
 * Dates are stored as UTC midnight representing a calendar date
 * Example: 2025-11-24T00:00:00.000Z -> "2025-11-24" (in user's timezone)
 */
export const utcDateToUserTimezoneDateString = (utcDate: Date | string): string => {
  const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
  const timezone = getUserTimezone();
  
  // Extract UTC date components (these represent the calendar date)
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();
  
  // Create a date at noon UTC for this calendar date to avoid DST issues
  const dateAtNoon = new Date(Date.UTC(year, month, day, 12, 0, 0, 0));
  
  // Format in user's timezone
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  
  return formatter.format(dateAtNoon);
};

/**
 * Convert a date string (YYYY-MM-DD) from user's timezone to UTC Date object
 * The returned date will be UTC midnight representing that calendar date
 */
export const userTimezoneDateStringToUTC = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  // Store as UTC midnight for the calendar date
  return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
};

/**
 * Get day of week for a calendar date in user's timezone
 * Returns 0-6 (Sunday=0, Monday=1, etc.)
 * 
 * CRITICAL: For "today" calculations, use local date components, not UTC
 * This prevents timezone issues where UTC date is already tomorrow
 */
export const getDayOfWeekInUserTimezone = (utcDate: Date | string): number => {
  const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
  const timezone = getUserTimezone();
  
  // CRITICAL FIX: For "today" (current date), use local date components
  // For stored dates (UTC midnight), use UTC components
  const now = new Date();
  const isToday = date.getTime() >= now.getTime() - 86400000 && date.getTime() <= now.getTime() + 86400000; // Within 24 hours
  
  let year: number, month: number, day: number;
  if (isToday) {
    // For "today", use local date components to get correct day of week
    year = date.getFullYear();
    month = date.getMonth();
    day = date.getDate();
  } else {
    // For stored dates (UTC midnight), use UTC components
    year = date.getUTCFullYear();
    month = date.getUTCMonth();
    day = date.getUTCDate();
  }
  
  // Create date at noon in the appropriate timezone for this calendar date
  // Use local time for "today", UTC for stored dates
  const dateAtNoon = isToday 
    ? new Date(year, month, day, 12, 0, 0, 0) // Local noon
    : new Date(Date.UTC(year, month, day, 12, 0, 0, 0)); // UTC noon
  
  // Get day of week in user's timezone
  const dayName = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    weekday: 'long'
  }).format(dateAtNoon);
  
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return dayNames.indexOf(dayName);
};

/**
 * Format a date for display in user's timezone
 * Returns MM/DD/YYYY format
 */
export const formatDateForDisplay = (utcDate: Date | string): string => {
  const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
  const timezone = getUserTimezone();
  
  // Extract UTC date components
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();
  
  // Create date at noon UTC for this calendar date
  const dateAtNoon = new Date(Date.UTC(year, month, day, 12, 0, 0, 0));
  
  // Format in user's timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  
  const parts = formatter.formatToParts(dateAtNoon);
  const monthPart = parts.find(p => p.type === 'month')?.value || '';
  const dayPart = parts.find(p => p.type === 'day')?.value || '';
  const yearPart = parts.find(p => p.type === 'year')?.value || '';
  
  return `${monthPart}/${dayPart}/${yearPart}`;
};

/**
 * Compare two dates to see if they represent the same calendar date
 * Uses UTC date components directly (authoritative) since dates are stored as UTC midnight
 * This is the STANDARD method for all date comparisons in the app
 * 
 * @param date1 - First date (Date object or ISO string)
 * @param date2 - Second date (Date object or ISO string)
 * @returns true if both dates represent the same calendar date
 */
export const isSameCalendarDate = (date1: Date | string, date2: Date | string): boolean => {
  const d1 = typeof date1 === 'string' ? new Date(date1) : date1;
  const d2 = typeof date2 === 'string' ? new Date(date2) : date2;
  
  // Extract UTC date components directly (authoritative)
  const year1 = d1.getUTCFullYear();
  const month1 = d1.getUTCMonth();
  const day1 = d1.getUTCDate();
  
  const year2 = d2.getUTCFullYear();
  const month2 = d2.getUTCMonth();
  const day2 = d2.getUTCDate();
  
  return year1 === year2 && month1 === month2 && day1 === day2;
};

/**
 * Format a contract date (UTC midnight = calendar date) for display without timezone shift.
 * Use for seasonal contract start/end so "Oct 1, 2026" shows as Oct 1, not Sep 30 in local TZ.
 *
 * @param date - Date object or ISO string (stored as UTC midnight)
 * @returns e.g. "Oct 1, 2026"
 */
export const formatContractDateUTC = (date: Date | string | null | undefined): string => {
  if (date == null) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

/**
 * Get UTC date string (YYYY-MM-DD) from a date
 * This is the standard format for date comparisons
 *
 * @param date - Date object or ISO string
 * @returns YYYY-MM-DD string in UTC
 */
export const getUTCDateString = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth();
  const day = d.getUTCDate();
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

/**
 * Check if a date is "today" (same calendar date as now)
 * Uses UTC date components for authoritative comparison
 * 
 * @param date - Date to check (Date object or ISO string)
 * @returns true if the date is today
 */
export const isToday = (date: Date | string): boolean => {
  const d = typeof date === 'string' ? new Date(date) : date;
  
  // Compare calendar dates in the user's timezone
  // This ensures "today" means today from the user's perspective, not UTC
  const dateInUserTZ = utcDateToUserTimezoneDateString(d);
  const todayInUserTZ = getTodayInUserTimezone();
  
  return dateInUserTZ === todayInUserTZ;
};

